import { DataType, Form, FormOptions, Section, SelectOption } from '@globaleris/ui.library'
import { Box, Typography } from '@material-ui/core'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  EnumTipoAgrupacion,
  EnumTipoCicloFacturacion,
  EnumTipoFacturacion,
  EnumTipoRefacturacion,
  EnumTipoSujeto
} from '../../../api'
import {
  enumToSelectOptionArray,
  enumToSelectOptionArrayDefaultValues
} from '../../../helpers/enumHelper'
import { getCustomerDomain } from '../../../redux'
import { CardActionProps } from '../cardActions/cardActions'
import SimpleDialog from '../simpleDialog/simpleDialog'

interface ExecutionCycleFormDialogProps {
  open: boolean
  onClose: (formData?: ExecutionCycleFormDataModel) => void
  /**
   * Pre-filled data for the form
   */
  initialData?: {
    cycleSelect?: number
    cycleName?: string
    startDate?: string
    endDate?: string
  }
  /**
   * Mode: 'close' hides cycle selection and shows simplified form
   * Mode: 'start' shows full form with cycle selection and additional fields
   */
  mode?: 'start' | 'close'
  /**
   * Available cycles for selection (required for mode='start')
   */
  availableCycles?: (SelectOption & {
    invoiceType: number
    invoiceCycleSubtype: number | undefined
  })[]
  /**
   * Loading state for cycles (used in mode='start')
   */
  isFetchingAvailableCycles?: boolean
  /**
   * Callback to update form data when cycle is selected (used in mode='start')
   */
  onFormDataUpdate?: (data: ExecutionCycleFormDataModel) => void
  /**
   * Props for mode='close' - required to execute cycle closure
   */
  closeExecutionCycleData?: {
    executionCycleId: number
    invoiceCycleId: number
    invoiceCycleType?: EnumTipoCicloFacturacion
    subjectType?: EnumTipoSujeto
    billingType?: EnumTipoFacturacion
    generateInvoices: boolean
  }
  /**
   * Hide date fields (used for annulment cycle selection where dates come from invoices)
   */
  hideDates?: boolean
  /**
   * Informative text shown below the form
   */
  footerNote?: string
}

export interface ExecutionCycleFormDataModel {
  cycleSelect?: number
  cycleName?: string
  invoiceCycleSubtype?: number
  startDate?: string
  endDate?: string
  reInvoiceType?: number
  groupingType?: number
}

const initialFormData: ExecutionCycleFormDataModel = {
  cycleSelect: undefined,
  cycleName: undefined,
  invoiceCycleSubtype: undefined,
  startDate: undefined,
  endDate: undefined,
  reInvoiceType: undefined,
  groupingType: undefined
}

const EMPTY_CYCLES: (SelectOption & {
  invoiceType: number
  invoiceCycleSubtype: number | undefined
})[] = []

/**
 * Formulario reutilizable para iniciar o cerrar ciclo de ejecución.
 * Puede usarse tanto en la pantalla principal como en el diálogo de cierre.
 *
 * Modo 'close': formulario simplificado solo con fechas
 * Modo 'start': formulario completo con selección de ciclo, reInvoiceType, groupingType
 */
export const ExecutionCycleFormDialog: React.FC<ExecutionCycleFormDialogProps> = ({
  open,
  onClose,
  initialData,
  mode = 'start',
  availableCycles = EMPTY_CYCLES,
  isFetchingAvailableCycles = false,
  onFormDataUpdate,
  closeExecutionCycleData,
  hideDates = false,
  footerNote
}) => {
  const [t] = useTranslation([
    'invoiceCyclesPage',
    'settlementsInvoices',
    'enums',
    'globalerisLibrary'
  ])

  const [formData, setFormData] = useState<ExecutionCycleFormDataModel>(initialFormData)
  const [formValid, setFormValid] = useState(false)
  const [isRefacturacion, setIsRefacturacion] = useState<boolean>(false)
  const [isAnuladoraNueva, setIsAnuladoraNueva] = useState<boolean>(false)

  const isCloseMode = mode === 'close'
  const isStartMode = mode === 'start'

  // For close mode, check if billing type is Refacturación (Anuladora does NOT show conditional fields)
  const isRefactInCloseMode =
    isCloseMode && closeExecutionCycleData?.billingType === EnumTipoFacturacion.Refacturacion

  // Show reInvoiceType and groupingType if:
  // - Start mode AND user selected Refacturación/Anuladora cycle
  // - Close mode AND billingType is Refacturación/Anuladora
  const showReInvoiceType = (isStartMode && isRefacturacion) || isRefactInCloseMode
  const showGroupingType =
    (isStartMode && isAnuladoraNueva) || (isRefactInCloseMode && isAnuladoraNueva)

  // Form sections definition
  const sections: Section[] = useMemo(
    () => [
      {
        title: isCloseMode
          ? 'Configuración del cierre de ciclo'
          : 'Selecciona el ciclo sin ejecución activa para lanzar',
        inputs: [
          // Cycle selection (only in start mode)
          {
            name: 'cycleSelect',
            label: t('invoiceCyclesPage:startExecution.cycleSelect', 'Ciclo de facturación'),
            type: DataType.SELECT,
            required: isStartMode,
            inputsize: 12,
            selectOptions: availableCycles,
            isLoading: isFetchingAvailableCycles,
            display: isStartMode,
            onChange: value => {
              const selectedCycle = availableCycles.find(cycle => cycle.value === +value)
              const isRefact = selectedCycle?.invoiceType === EnumTipoFacturacion.Refacturacion

              setIsRefacturacion(isRefact)

              const updatedData = {
                ...formData,
                cycleSelect: +value,
                invoiceCycleSubtype: selectedCycle?.invoiceCycleSubtype,
                cycleName: selectedCycle?.label
              }
              setFormData(updatedData)
              onFormDataUpdate?.(updatedData)
            }
          },
          // Cycle name (hidden field in start mode, visible in close mode)
          {
            name: 'cycleName',
            label: 'Ciclo de facturación',
            inputsize: 12,
            type: DataType.TEXT,
            required: false,
            display: isCloseMode,
            readOnly: true
          },
          // Re-invoice type (conditionally visible)
          {
            name: 'reInvoiceType',
            label: t('invoiceCyclesPage:startExecution.reInvoiceType', 'Tipo de refacturación'),
            type: DataType.SELECT,
            inputsize: 6,
            required: showReInvoiceType,
            selectOptions: enumToSelectOptionArrayDefaultValues(
              EnumTipoRefacturacion,
              'enums:EnumTipoRefacturacion',
              [1, 2, 3] // Exclude Anuladora (4) and Regeneradora (5)
            ),
            display: showReInvoiceType,
            onChange: value => {
              const isAnuladora = +value === EnumTipoRefacturacion.AnuladoraNueva
              setIsAnuladoraNueva(isAnuladora)
              setFormData(prev => ({
                ...prev,
                reInvoiceType: +value,
                // Reset groupingType if no longer AnuladoraNueva
                ...(isAnuladora ? {} : { groupingType: undefined })
              }))
            }
          },
          // Grouping type (conditionally visible when isAnuladoraNueva)
          {
            name: 'groupingType',
            label: t('invoiceCyclesPage:startExecution.groupingType', 'Tipo de agrupación'),
            type: DataType.SELECT,
            required: showGroupingType,
            inputsize: 6,
            selectOptions: enumToSelectOptionArray(EnumTipoAgrupacion, 'enums:EnumTipoAgrupacion'),
            display: showGroupingType,
            onChange: value => {
              setFormData(prev => ({ ...prev, groupingType: +value }))
            }
          },
          // Start date
          {
            name: 'startDate',
            label: t('invoiceCyclesPage:startExecution.startDate', 'Fecha inicio'),
            type: DataType.DATE,
            inputsize: 6,
            required: !hideDates,
            display: !hideDates,
            onChange: value => {
              // Ignore null: during keyboard typing the picker fires null for incomplete dates,
              // which would reset the controlled input. Only commit valid (non-null) values.
              if (value != null) setFormData(prev => ({ ...prev, startDate: value.toString() }))
            }
          },
          // End date
          {
            name: 'endDate',
            label: t('invoiceCyclesPage:startExecution.endDate', 'Fecha fin'),
            type: DataType.DATE,
            inputsize: 6,
            required: !hideDates,
            display: !hideDates,
            onChange: value => {
              if (value != null) setFormData(prev => ({ ...prev, endDate: value.toString() }))
            }
          }
        ]
      }
    ],
    [
      t,
      isCloseMode,
      isStartMode,
      availableCycles,
      isFetchingAvailableCycles,
      showReInvoiceType,
      showGroupingType,
      formData,
      onFormDataUpdate
    ]
  )

  // Form options
  const formOptions: FormOptions = useMemo(
    () => ({
      elevation: 0,
      columns: 2,
      analytics: {
        customer: getCustomerDomain(),
        view: isCloseMode
          ? 'settlementsInvoices:closeExecutionCycleFormDialog'
          : 'invoiceCyclesPage:startExecutionFormDialog'
      }
    }),
    [isCloseMode]
  )

  // Form validation
  useEffect(() => {
    let valid = hideDates ? true : !!formData.startDate && !!formData.endDate

    if (isStartMode) {
      valid = valid && !!formData.cycleSelect
      if (showReInvoiceType) {
        valid = valid && !!formData.reInvoiceType
      }
      if (showGroupingType) {
        valid = valid && !!formData.groupingType
      }
    }

    setFormValid(valid)
  }, [formData, isStartMode, showReInvoiceType, showGroupingType, hideDates])

  // Handle close action
  const handleCloseAction = async () => {
    if (!formValid) return
    onClose(formData as any)
  }

  // Footer actions for SimpleDialog
  const footerActions: CardActionProps[] = useMemo(
    () => [
      {
        label: t('globalerisLibrary:form.actions.cancel'),
        color: 'secondary',
        handleActionClick: () => onClose(undefined)
      },
      {
        label: isStartMode
          ? t('invoiceCyclesPage:startExecution.launch', 'Lanzar')
          : t('globalerisLibrary:form.actions.save'),
        color: 'primary',
        disabled: !formValid,
        handleActionClick: handleCloseAction
      }
    ],
    [t, isStartMode, formValid, onClose]
  )

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      const preSelected = initialData?.cycleSelect
        ? availableCycles.find(c => c.value === initialData.cycleSelect)
        : undefined

      setFormData({
        ...initialFormData,
        ...initialData,
        ...(preSelected && {
          cycleName: preSelected.label,
          invoiceCycleSubtype: preSelected.invoiceCycleSubtype
        })
      })

      const isRefactOrAnuladora = preSelected?.invoiceType === EnumTipoFacturacion.Refacturacion

      setIsRefacturacion(isRefactOrAnuladora ?? false)
      setIsAnuladoraNueva(false)
      setFormValid(false)
    }
  }, [open, initialData, availableCycles])

  return (
    <SimpleDialog
      open={open}
      handleClose={() => onClose(undefined)}
      footerActions={footerActions}
      maxWidth="md"
      formStyle="formDialog"
    >
      <Form
        sections={sections}
        data={formData}
        options={formOptions}
        title={
          isCloseMode
            ? 'Cerrar ciclo de ejecución'
            : t('invoiceCyclesPage:startExecution.title', 'Iniciar Ejecución')
        }
      />
      {footerNote && (
        <Box px={2} pt={2} pb={1}>
          <Typography variant="caption" style={{ color: '#9e9e9e' }}>
            * {footerNote}
          </Typography>
        </Box>
      )}
    </SimpleDialog>
  )
}
